/**
 * What the Plans page knows about each kind of plan, and the conversions between a plan, the
 * editor's form state and the save request.
 *
 * Member and business plans share their identity, pricing and availability fields and differ only
 * in their limits and feature switches, so those two lists are data here and one editor draws both.
 */

import type { AdminSubscriptionPlanDto, BusinessSubscriptionPlanDto } from '@api/types/plans'

export type PlanKind = 'consumer' | 'business'
export type AnyPlan = AdminSubscriptionPlanDto | BusinessSubscriptionPlanDto

/** The in-depth explanation behind a setting's (?) icon. */
export interface FieldHelpInfo {
  /** Paragraphs, shown in order. */
  body: string[]
  /**
   * What the app does with the setting today. Anything other than `enforced` is badged in the
   * popover, so a limit that is only stored is never mistaken for one that is applied.
   */
  enforcement?: 'enforced' | 'display-only' | 'not-used'
}

export interface LimitField {
  key: string
  label: string
  helper?: string
  /** Stored in bytes, edited in MB. */
  unit?: 'bytes'
  /**
   * The API has no "unlimited" for this limit (the column is not nullable): the editor hides the
   * Unlimited box and always sends a number, 0 meaning none.
   */
  noUnlimited?: boolean
  help?: FieldHelpInfo
}

export interface FeatureField {
  key: string
  label: string
  helper?: string
  help?: FieldHelpInfo
}

/** The editor fields both kinds of plan share, each of which can carry help. */
export type CommonField =
  | 'displayName'
  | 'name'
  | 'quickDescription'
  | 'fullHtmlDescription'
  | 'monthlyPrice'
  | 'annualPrice'
  | 'currency'
  | 'trialPeriodDays'
  | 'isActive'
  | 'isFreeTier'
  | 'sortOrder'

export interface PlanKindSpec {
  kind: PlanKind
  /** "plan" / "business plan", for titles and confirmations. */
  noun: string
  limits: LimitField[]
  features: FeatureField[]
  /** Help for the shared fields. Kept per kind: the free plan, trials and billing mean different things for businesses. */
  help: Partial<Record<CommonField, FieldHelpInfo>>
}

export const CONSUMER_SPEC: PlanKindSpec = {
  kind: 'consumer',
  noun: 'plan',
  help: {
    displayName: {
      body: [
        "The plan's name on the Website's Subscription page (account menu → Subscription → Plans) and wherever a person's plan is shown. The app has no subscription screens.",
        'Some messages name Explorer in their own wording — "The trip planner is included with Explorer and above", and the Website\'s "Planning a trip needs Explorer" — so renaming Explorer here does not update them.',
      ],
    },
    name: {
      body: [
        'The internal identifier, fixed once the plan exists.',
        'Location analytics sorts people into tiers by it: "adventurer" counts as premium, "explorer" as basic, and any other name as free. A new paid plan with a different name is counted as free in analytics.',
        'The plan seeder also finds plans by name, so a renamed seeded plan would come back as a duplicate on the next deploy. Change the display name instead.',
      ],
    },
    quickDescription: {
      enforcement: 'display-only',
      body: [
        "The one-line summary on the plan's card on the Website's Plans tab, up to 180 characters. Not shown in the app.",
      ],
    },
    fullHtmlDescription: {
      enforcement: 'display-only',
      body: [
        "Rendered as HTML under the plan's card on the Website's Plans tab. Each <li> gets a green tick, so a <ul> of what the plan includes works best. Not shown in the app.",
        'It is inserted into the page without sanitising and the plan list is public, so only paste HTML you wrote or trust.',
      ],
    },
    monthlyPrice: {
      enforcement: 'display-only',
      body: [
        "Shown on the Website's Plans tab. Nothing charges it: no payment processor is connected, and the Upgrade and Start Free Trial buttons don't do anything yet.",
        "Today people only get a paid plan from an admin (on their Subscription tab under Users) or by redeeming a credit an admin granted.",
      ],
    },
    annualPrice: {
      enforcement: 'display-only',
      body: [
        'Shown as the yearly option. The Monthly/Annual switch on the Plans tab only appears when at least one plan has an annual price, and the Website works out "Save N%" against the monthly price. Never charged.',
        'Leave it empty to offer no annual price.',
      ],
    },
    currency: {
      enforcement: 'display-only',
      body: [
        'Formats the prices on the plan cards: USD, EUR and GBP get their symbols and anything else is shown as "9.99 CODE".',
        'The per-month figure the Website shows on the annual view always uses "$" whatever this says.',
      ],
    },
    trialPeriodDays: {
      enforcement: 'display-only',
      body: [
        'Shown as a "N-day free trial" badge, and it turns the plan\'s button into "Start Free Trial" — but nothing a member can do starts a trial yet.',
        'An admin can put someone on a trial from their Subscription tab; that uses the length the admin chooses, not this number.',
        "During a trial a person gets all of this plan's features. When it ends they lose them immediately and are moved to the free plan within the hour.",
      ],
    },
    isActive: {
      enforcement: 'enforced',
      body: [
        'Inactive plans are left out of the public plan list, so nobody new is offered them.',
        'Everyone already on the plan keeps it, with all its features, until their subscription lapses. Admins can still put people on it, and credits can still be granted and redeemed for it.',
        'The free plan must stay active: accounts and lapsed subscriptions are placed on the active free plan, and without one subscription lookups fail.',
      ],
    },
    isFreeTier: {
      enforcement: 'enforced',
      body: [
        "The plan everyone without a paid subscription is on. An account is given it the first time its subscription is looked up, and every lapsed subscription, ended trial and downgrade lands here. There can only be one, it must cost nothing, and it must stay active.",
        "Switching features on for the free plan gives nobody anything: features are only granted to subscriptions that are active or on trial, and free-plan subscriptions have their own Free status. For the same reason people on the free plan can't create community events.",
        'Separately, anyone without an active paid subscription can keep 5 saved trails and can\'t share trails. Those rules are fixed in the app, not settings on any plan.',
      ],
    },
    sortOrder: {
      body: [
        "The order of the plan cards on the Website's Plans tab, lowest first. Plans with the same number are ordered by monthly price.",
      ],
    },
  },
  // Help text is written from the code that reads each setting (verified 2026-09-14). When a
  // setting starts or stops being enforced, update its `enforcement` and body here.
  limits: [
    {
      key: 'maxStorageSize',
      label: 'Storage',
      unit: 'bytes',
      helper: 'File uploads',
      help: {
        enforcement: 'not-used',
        body: [
          'Sent to the apps with the plan, but no upload is checked against it.',
          "What actually limits someone's files is their personal storage quota, which is 50 MB and 50 files for everyone whatever their plan. Raising this number gives nobody more space.",
          'Entered here in MB and saved in bytes.',
        ],
      },
    },
    {
      key: 'maxPremiumAdventures',
      label: 'Premium adventures',
      help: {
        enforcement: 'not-used',
        body: [
          'Nothing counts premium adventures, so this number is never consulted.',
          'Access to an adventure marked as needing a subscription is all or nothing: it depends on whether the person\'s subscription counts as active, not on how many premium adventures they have played. Without it they see "This is a premium adventure. It is not available at your account level."',
        ],
      },
    },
    {
      key: 'maxGroupSize',
      label: 'Group size',
      helper: 'Members in a group they own',
      help: {
        enforcement: 'not-used',
        body: [
          "No group size check reads this. A group's capacity is the Max members setting on the group itself, chosen by its owner or editors; joining past that gets \"Group is full\" whatever anyone's plan says.",
        ],
      },
    },
    {
      key: 'maxGroupSizePaidTier',
      label: 'Group size, paid members',
      help: {
        enforcement: 'not-used',
        body: [
          'Meant to set a different group cap for members who pay, alongside Group size, free members. It was never built: nothing reads either value, and groups are capped only by their own Max members setting.',
        ],
      },
    },
    {
      key: 'maxGroupSizeFreeTier',
      label: 'Group size, free members',
      help: {
        enforcement: 'not-used',
        body: [
          'Meant to set a different group cap for members on the free plan, alongside Group size, paid members. It was never built: nothing reads either value, and groups are capped only by their own Max members setting.',
        ],
      },
    },
    {
      key: 'maxEventSize',
      label: 'Event size',
      helper: 'Participants per event',
      help: {
        enforcement: 'enforced',
        body: [
          'Caps the attendee limit on events owned by someone on this plan. When they edit an event\'s attendee limit it is quietly lowered to this number — no error is shown — and an empty attendee limit becomes this number.',
          'It is not applied when an event is first created: a new event keeps the attendee limit that was typed until it is next edited.',
          'It applies while the owner\'s subscription is active or on a trial of this plan. People on the free plan or with no subscription get no cap, and neither do admins. Events owned by groups or businesses are not affected. Here both 0 and Unlimited mean no cap.',
        ],
      },
    },
    {
      key: 'maxEventsPerMonth',
      label: 'Events per month',
      help: {
        enforcement: 'enforced',
        body: [
          'How many community events someone on this plan can create in a calendar month (UTC, resetting on the 1st). Every event they own created that month counts, personal reminders included, although creating a reminder is never blocked.',
          'Unlimited means no cap. 0 means this plan can\'t create community events at all.',
          'It applies while the person\'s subscription is active or on a trial of this plan. People on the free plan or with no subscription can\'t create community events whatever this says. Admins are never limited.',
          'Reaching the limit is refused with "You have reached your monthly event creation limit".',
        ],
      },
    },
    {
      key: 'publicEventsPerMonth',
      label: 'Public events per month',
      help: {
        enforcement: 'not-used',
        body: [
          'Nothing checks whether an event is public against the plan. Public events simply count toward Events per month like any other event.',
        ],
      },
    },
    {
      key: 'maxGiftsPerMonth',
      label: 'Gifts per month',
      help: {
        enforcement: 'not-used',
        body: ['Gift rewards are not counted or limited by plan anywhere, so this number has no effect.'],
      },
    },
    {
      key: 'maxQrRedeemables',
      label: 'QR redeemable codes',
      help: {
        enforcement: 'not-used',
        body: ['Rewards redeemed by QR code are not counted or limited by plan anywhere, so this number has no effect.'],
      },
    },
    {
      key: 'aiNarrationCharactersPerMonth',
      label: 'AI narration characters per month',
      helper: 'Script characters, tags included. 0 = none',
      noUnlimited: true,
      help: {
        enforcement: 'enforced',
        body: [
          'How many characters of AI narration someone on this plan can generate each calendar month (UTC, resetting on the 1st). Only applies when AI narration is switched on below.',
          'What counts is the script as it is sent to ElevenLabs, which is what ElevenLabs bills: whitespace tidied, emotive [tags] included. Previews count too. A failed generation costs nothing, and generating the same script and voice again reuses the earlier audio for free.',
          'When the allowance would be exceeded the request is refused with how many characters are left and when the month resets. 0 means none: AI narration is effectively off even with the switch on. There is no unlimited setting.',
          'Site admins are never limited (their use is still recorded). Moderators are limited like anyone else.',
        ],
      },
    },
  ],
  // Every feature below is granted only while a subscription is active or on trial (see the
  // free plan's help), and trials include all of the plan's features.
  features: [
    {
      key: 'hasTripPlanner',
      label: 'Trip planner',
      helper: 'Adventures worth stopping for along a route',
      help: {
        enforcement: 'enforced',
        body: [
          'Lets people plan a trip: creating one, and getting suggestions for adventures worth stopping for along the route. Without it the server refuses with "The trip planner is included with Explorer and above." and both the Website and the app show the planner locked.',
          "Viewing a trip, accepting an invite to one and opening a trip someone shared stay free. Someone invited as an editor who doesn't have the planner can only view.",
        ],
      },
    },
    {
      key: 'hasGhostRacers',
      label: 'Ghost racers',
      helper: 'Race the top ten past runs of a route',
      help: {
        enforcement: 'enforced',
        body: [
          'Lets people race the ghosts of the top ten past runs while walking a route. Without it the app simply hides the ghost control — it does not explain why.',
          "On the standalone trail walk the race is built from runs stored on the phone, so there the app's own check is the only gate. The Website has no ghost racers.",
        ],
      },
    },
    {
      key: 'hasGroupRecording',
      label: 'Host group recordings',
      helper: 'Joining one is always free',
      help: {
        enforcement: 'enforced',
        body: [
          "Lets someone START a group hike, where one phone records the track and everyone else adds waypoints and photos. Joining someone else's group is always free, so one person on this plan is enough for the whole group.",
          "Online groups are checked by the server when the session is created. Nearby (Bluetooth) groups have no server involved, so there the app's own check is the only gate.",
          "A change reaches someone when their app next refreshes its sign-in, not the moment you save.",
        ],
      },
    },
    {
      key: 'hasDriveNavigation',
      label: 'Start drives',
      helper: 'Joining a drive is always free',
      help: {
        enforcement: 'enforced',
        body: [
          'Lets someone START a drive: a shared destination several people navigate to together, seeing each other\'s position and arrival time. Without it starting is refused with "Starting a drive is included with Explorer and above." Being invited to and joining a drive is always free.',
          "Neither the Website nor the released app has drive screens yet, so for now this only affects the API.",
        ],
      },
    },
    {
      key: 'hasAiNarration',
      label: 'AI narration',
      helper: 'Creators generate voice narration with ElevenLabs',
      help: {
        enforcement: 'enforced',
        body: [
          "Lets creators turn a written script into spoken narration with ElevenLabs, using a voice from the Narration page: for a step's narration, an audio step, or a Story's or adventure's description. They can add emotive [tags] like [whispers].",
          'It is spent from AI narration characters per month, so switch this on AND give the plan an allowance. Without it generating is refused with "AI narration isn\'t available on your account."',
          'Granted while the subscription is active or on a trial of this plan. Site admins can always generate; moderators cannot without it. Playing narration that was already generated is always free for everyone.',
        ],
      },
    },
    {
      key: 'hasGroupChat',
      label: 'Group chat',
      help: {
        enforcement: 'not-used',
        body: ['Nothing reads this. Access to a group\'s chat comes from being a member of the group, whatever the plan.'],
      },
    },
    {
      key: 'hasTicketManagement',
      label: 'Ticket management',
      help: {
        enforcement: 'not-used',
        body: ['Sent to the apps with the plan list, but no ticket or support feature checks it.'],
      },
    },
    {
      key: 'hasBusinessFeatures',
      label: 'Business features',
      help: {
        enforcement: 'not-used',
        body: [
          'Nothing reads this. Businesses have their own plans, on the Business tab, and are never limited by a member plan.',
        ],
      },
    },
  ],
}

export const BUSINESS_SPEC: PlanKindSpec = {
  kind: 'business',
  noun: 'business plan',
  // Written from the code that reads each setting (verified 2026-09-14). Business plans are
  // reachable only through the API today: neither the Website nor the app has a business plan
  // screen, and nothing puts a new business on a plan automatically.
  help: {
    displayName: {
      body: [
        'The plan name returned wherever business plans are listed. No screen in the Website or the app lists business plans yet, so today it is only seen through the API and this panel.',
      ],
    },
    name: {
      body: [
        'The internal identifier. It is fixed once the plan exists so anything referring to the plan by name keeps working — change the display name instead.',
        'Lowercase letters, numbers and dashes, up to 100 characters, and unique among business plans.',
      ],
    },
    quickDescription: {
      enforcement: 'display-only',
      body: ['Returned with the plan list, up to 180 characters. No screen shows business plans yet.'],
    },
    fullHtmlDescription: {
      enforcement: 'display-only',
      body: [
        'Longer HTML description returned with the plan. No screen shows business plans yet, so how it renders depends on whichever screen is built to show it.',
      ],
    },
    monthlyPrice: {
      enforcement: 'display-only',
      body: [
        'No payment is taken for business plans. A business owner or admin can subscribe, change plan and reactivate instantly and for free; the price is only shown.',
        'Choosing monthly or annual billing only decides whether each period lasts a month or a year.',
      ],
    },
    annualPrice: {
      enforcement: 'display-only',
      body: [
        'Shown as the yearly option. Like the monthly price it is never charged; choosing annual billing just makes each period last a year.',
        'Leave it empty to offer no annual price.',
      ],
    },
    currency: {
      enforcement: 'display-only',
      body: [
        'Used to format the prices: USD, EUR and GBP get their symbols and anything else is written as "0.00 CODE".',
        "A business's own subscription view currently shows \"$\" whatever this says.",
      ],
    },
    trialPeriodDays: {
      enforcement: 'enforced',
      body: [
        'Applies only when a business subscribes for the first time, with no subscription yet: it starts as a trial that ends after this many days. Switching an existing subscription to this plan skips the trial and makes it active straight away.',
        'When the trial ends, the hourly expiry job moves the business to the free business plan. 0 means no trial.',
      ],
    },
    isActive: {
      enforcement: 'enforced',
      body: [
        'Inactive business plans disappear from the plan list and can\'t be subscribed or switched to ("Plan not found or inactive").',
        'Businesses already on the plan keep it. An inactive plan is also never used as the free plan for downgrades.',
      ],
    },
    isFreeTier: {
      enforcement: 'enforced',
      body: [
        'Where business subscriptions go when their period or trial ends. There can only be one, it must cost nothing, and once it exists it must stay active.',
        'Without an active free business plan, a lapsed business is marked Expired but keeps its paid plan attached: features read as off, yet its location and team member caps still use the paid plan\'s numbers.',
        'New businesses are not put on this plan automatically. A business with no subscription has no limits at all.',
      ],
    },
    sortOrder: {
      body: ['The order of the business plan list, lowest first. Plans with the same number have no guaranteed order.'],
    },
  },
  limits: [
    {
      key: 'maxStorageSize',
      label: 'Storage',
      unit: 'bytes',
      helper: 'File uploads',
      help: {
        enforcement: 'not-used',
        body: [
          'Shown as the plan\'s storage limit, but no business upload is checked against it.',
          'Entered here in MB and saved in bytes.',
        ],
      },
    },
    {
      key: 'maxLocations',
      label: 'Locations',
      help: {
        enforcement: 'enforced',
        body: [
          'The most active locations a business on this plan can have. Adding one past it is refused with "Maximum location limit (n) reached. Upgrade your subscription to add more locations." 0 allows no new locations; Unlimited removes the cap.',
          'Gaps: the first location created along with the business isn\'t checked, reactivating a deactivated location isn\'t checked, and the cap keeps applying after the subscription expires. A business with no subscription has no cap.',
        ],
      },
    },
    {
      key: 'maxTeamMembers',
      label: 'Team members',
      help: {
        enforcement: 'enforced',
        body: [
          'Checked when a business owner or admin sends a team invite: once active members reach this number the invite is refused with "Maximum team member limit (n) reached. Upgrade your subscription to add more team members." 0 allows no invites; Unlimited removes the cap.',
          'Pending invitations don\'t count, so several outstanding invites can take a team past the limit when they are accepted. Accepting an invite is not checked.',
        ],
      },
    },
    {
      key: 'maxEventsPerMonth',
      label: 'Events per month',
      help: {
        enforcement: 'not-used',
        body: [
          'Shown on the business\'s subscription, but events owned by a business are never counted or limited.',
          'This is separate from the member plan\'s Events per month, which does limit events people create themselves.',
        ],
      },
    },
  ],
  features: [
    {
      key: 'canCreateEvents',
      label: 'Create events',
      help: {
        enforcement: 'not-used',
        body: [
          'Businesses can create events whether or not this is on. The only check is that the person creating it is an owner or admin of the business.',
        ],
      },
    },
    {
      key: 'canRunAdventureTests',
      label: 'Run adventure tests',
      help: {
        enforcement: 'not-used',
        body: ['There is no business adventure-testing feature for this to switch on yet.'],
      },
    },
    {
      key: 'canUseAnalytics',
      label: 'Analytics',
      help: {
        enforcement: 'not-used',
        body: ['Shown on the business\'s subscription, but no analytics screen or endpoint checks it.'],
      },
    },
    {
      key: 'canUseBranding',
      label: 'Custom branding',
      help: {
        enforcement: 'not-used',
        body: ['Shown on the business\'s subscription, but no branding feature checks it.'],
      },
    },
    {
      key: 'prioritySupport',
      label: 'Priority support',
      help: {
        enforcement: 'not-used',
        body: [
          'Shown on the business\'s subscription, but the support ticket queue doesn\'t read it, so their tickets are not prioritised.',
        ],
      },
    },
  ],
}

export const planQueryKey = (kind: PlanKind) => ['admin-plans', kind]

const MB = 1024 * 1024
export const QUICK_DESCRIPTION_MAX = 180
const NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/

/**
 * Editor state. Numbers are held as the text typed, so a half-typed "9." survives a re-render;
 * a limit is `null` while "Unlimited" is ticked.
 */
export interface PlanDraft {
  name: string
  displayName: string
  quickDescription: string
  fullHtmlDescription: string
  monthlyPrice: string
  annualPrice: string
  currency: string
  trialPeriodDays: string
  sortOrder: string
  isActive: boolean
  isFreeTier: boolean
  limits: Record<string, string | null>
  features: Record<string, boolean>
}

const field = (plan: AnyPlan, key: string): unknown => (plan as unknown as Record<string, unknown>)[key]

export function blankDraft(spec: PlanKindSpec, sortOrder: number): PlanDraft {
  return {
    name: '',
    displayName: '',
    quickDescription: '',
    fullHtmlDescription: '',
    monthlyPrice: '0',
    annualPrice: '',
    currency: 'USD',
    trialPeriodDays: '0',
    sortOrder: String(sortOrder),
    isActive: true,
    isFreeTier: false,
    // A new plan starts unlimited everywhere; you narrow it, rather than hunt for the limit you forgot.
    // A limit with no unlimited (AI narration characters) starts at 0, i.e. none.
    limits: Object.fromEntries(spec.limits.map((l) => [l.key, l.noUnlimited ? '0' : null])),
    features: Object.fromEntries(spec.features.map((f) => [f.key, false])),
  }
}

export function draftFromPlan(plan: AnyPlan, spec: PlanKindSpec): PlanDraft {
  return {
    name: plan.name,
    displayName: plan.displayName,
    quickDescription: plan.quickDescription ?? '',
    fullHtmlDescription: plan.fullHtmlDescription ?? '',
    monthlyPrice: String(plan.monthlyPrice),
    annualPrice: plan.annualPrice == null ? '' : String(plan.annualPrice),
    currency: plan.currency,
    trialPeriodDays: String(plan.trialPeriodDays),
    sortOrder: String(plan.sortOrder),
    isActive: plan.isActive,
    isFreeTier: plan.isFreeTier,
    limits: Object.fromEntries(
      spec.limits.map((l) => {
        const value = field(plan, l.key) as number | null
        if (value == null) return [l.key, l.noUnlimited ? '0' : null]
        return [l.key, l.unit === 'bytes' ? String(Math.round((value / MB) * 100) / 100) : String(value)]
      }),
    ),
    features: Object.fromEntries(spec.features.map((f) => [f.key, Boolean(field(plan, f.key))])),
  }
}

/** The save body. Shape matches SaveSubscriptionPlanRequest / SaveBusinessSubscriptionPlanRequest for the spec's kind. */
export function requestFromDraft(draft: PlanDraft, spec: PlanKindSpec): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: draft.name.trim(),
    displayName: draft.displayName.trim(),
    quickDescription: draft.quickDescription.trim(),
    fullHtmlDescription: draft.fullHtmlDescription.trim() === '' ? null : draft.fullHtmlDescription,
    monthlyPrice: Number(draft.monthlyPrice),
    annualPrice: draft.annualPrice.trim() === '' ? null : Number(draft.annualPrice),
    currency: draft.currency.trim().toUpperCase(),
    trialPeriodDays: Number(draft.trialPeriodDays),
    sortOrder: Number(draft.sortOrder),
    isActive: draft.isActive,
    isFreeTier: draft.isFreeTier,
  }
  for (const l of spec.limits) {
    const raw = draft.limits[l.key]
    // A noUnlimited limit is always sent as a number: for AI narration the API reads null as
    // "keep the stored value", so null would silently fail to save a change.
    if (raw == null) body[l.key] = l.noUnlimited ? 0 : null
    else body[l.key] = l.unit === 'bytes' ? Math.round(Number(raw) * MB) : Number(raw)
  }
  for (const f of spec.features) body[f.key] = draft.features[f.key]
  return body
}

const isNumber = (text: string) => text.trim() !== '' && Number.isFinite(Number(text))
const isWhole = (text: string) => isNumber(text) && Number.isInteger(Number(text))

/**
 * The same rules the server enforces, checked first so most mistakes show next to their field.
 * The server stays authoritative — its message is shown if something gets past these.
 */
export function validateDraft(draft: PlanDraft, spec: PlanKindSpec, creating: boolean): Record<string, string> {
  const errors: Record<string, string> = {}

  if (creating && !NAME_PATTERN.test(draft.name.trim())) {
    errors.name = 'Lowercase letters, numbers and dashes, starting with a letter or number.'
  }
  if (draft.displayName.trim() === '') errors.displayName = 'Required.'
  if (draft.quickDescription.trim().length > QUICK_DESCRIPTION_MAX) {
    errors.quickDescription = `At most ${QUICK_DESCRIPTION_MAX} characters.`
  }

  if (!isNumber(draft.monthlyPrice) || Number(draft.monthlyPrice) < 0) errors.monthlyPrice = 'A price of 0 or more.'
  if (draft.annualPrice.trim() !== '' && (!isNumber(draft.annualPrice) || Number(draft.annualPrice) < 0)) {
    errors.annualPrice = 'A price of 0 or more, or leave it empty.'
  }
  if (draft.isFreeTier && !errors.monthlyPrice && Number(draft.monthlyPrice) !== 0) {
    errors.monthlyPrice = "The free plan can't have a price."
  }
  if (draft.isFreeTier && !errors.annualPrice && draft.annualPrice.trim() !== '' && Number(draft.annualPrice) !== 0) {
    errors.annualPrice = "The free plan can't have a price."
  }
  if (!/^[A-Za-z]{3}$/.test(draft.currency.trim())) errors.currency = 'A three-letter code, such as USD.'
  if (!isWhole(draft.trialPeriodDays) || Number(draft.trialPeriodDays) < 0 || Number(draft.trialPeriodDays) > 365) {
    errors.trialPeriodDays = 'Whole days, 0 to 365.'
  }
  if (!isWhole(draft.sortOrder)) errors.sortOrder = 'A whole number.'

  for (const l of spec.limits) {
    const raw = draft.limits[l.key]
    if (raw == null) continue
    const ok = l.unit === 'bytes' ? isNumber(raw) : isWhole(raw)
    if (!ok || Number(raw) < 0) {
      errors[`limit:${l.key}`] = l.unit === 'bytes' ? '0 MB or more.' : 'A whole number, 0 or more.'
    }
  }

  return errors
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export function formatBytes(bytes: number | null): string {
  if (bytes == null) return 'Unlimited'
  if (bytes >= 1024 * MB) return `${Math.round((bytes / (1024 * MB)) * 10) / 10} GB`
  return `${Math.round(bytes / MB)} MB`
}

export function enabledFeatureLabels(plan: AnyPlan, spec: PlanKindSpec): string[] {
  return spec.features.filter((f) => Boolean(field(plan, f.key))).map((f) => f.label)
}

/** Why a plan can't be deleted, or null if it can (as far as the page knows — business usage is only checked by the server). */
export function deleteBlocker(plan: AnyPlan, spec: PlanKindSpec): string | null {
  if (plan.isFreeTier) return "The free plan can't be deleted: lapsed subscriptions fall back to it."
  if (spec.kind === 'consumer') {
    const p = plan as AdminSubscriptionPlanDto
    if (p.subscriberCount + p.paymentCount + p.creditCount > 0) {
      return 'Subscriptions, payments or credits refer to this plan. Deactivate it instead.'
    }
  }
  return null
}

/**
 * The API's own sentence for a refused save, which is written to be shown as it is. Axios's
 * "Request failed with status code 400" is the last resort, not the first.
 */
export function planErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (data && typeof data === 'object') {
    const d = data as { message?: string; Message?: string; errors?: Record<string, string[]>; title?: string }
    if (d.message) return d.message
    if (d.Message) return d.Message
    const first = d.errors ? Object.values(d.errors).flat()[0] : undefined
    if (first) return first
    if (d.title) return d.title
  }
  return err instanceof Error && err.message ? err.message : fallback
}
